/** Styles owned by the deployment picker: its panel, its search box, its rows and shell. */
import { spacing, Text } from '@scality/core-ui';
import styled, { css } from 'styled-components';
import { SELECT_FIELD_WIDTH, SelectMenuPanel, SelectMenuRow, SelectMenuScroll, SelectWrapper } from '../../../select';

/*
 * List height is specified in entries, not pixels: `account - name - PRODUCT` labels wrap,
 * so an entry is budgeted two lines. 3.25 entries stay visible — the quarter is deliberate,
 * a partially-cut row is what tells the reader the list scrolls.
 */
const MENU_ITEM_LINE_HEIGHT_REM = 1.25;
const MENU_ITEM_LINES = 2;
/** `spacing.r8` top + bottom on each item. */
const MENU_ITEM_PADDING_REM = 1;
const VISIBLE_ITEMS = 3.25;
const MENU_ITEM_HEIGHT_REM = MENU_ITEM_LINE_HEIGHT_REM * MENU_ITEM_LINES + MENU_ITEM_PADDING_REM;
const LIST_MAX_HEIGHT_REM = MENU_ITEM_HEIGHT_REM * VISIBLE_ITEMS;

/**
 * Toolbar width: the same box whether a chip or the placeholder is inside, which keeps the
 * row from reflowing when a deployment is picked. Shared with the filter bar's controls.
 */
const COMPACT_TRIGGER_WIDTH = SELECT_FIELD_WIDTH;

/** The shared panel, sized for the deployment list and padded around its search box. */
export const DeploymentMenuPanel = styled(SelectMenuPanel)`
	padding: ${spacing.r8};
	width: 21.5rem;
	max-height: 24rem;
`;

export const SearchWrapper = styled.div`
	flex-shrink: 0;
	padding: ${spacing.r8};
	border-bottom: 1px solid ${(p) => p.theme.border};
`;

/** Positions the in-field spinner; the input itself only accepts an icon *name*, not a node. */
export const SearchField = styled.div`
	position: relative;
`;

export const SearchSpinner = styled.span`
	position: absolute;
	right: ${spacing.r8};
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	pointer-events: none;
`;

export const CounterText = styled(Text)`
	display: block;
	margin-top: ${spacing.r4};
`;

export const Shell = styled(SelectWrapper)<{ $compact: boolean }>`
	${(p) =>
    p.$compact &&
    css`
			width: ${COMPACT_TRIGGER_WIDTH};
			min-width: 0;
			flex-shrink: 1;
		`}
`;

/** Bounds the label so ConstrainedText has a width to clamp against inside the flex trigger. */
export const ChipLabel = styled.span<{ $compact: boolean }>`
	min-width: 0;
	max-width: ${(p) => (p.$compact ? '100%' : '18rem')};
	overflow: hidden;
`;

/**
 * The placeholder truncates with a plain ellipsis rather than ConstrainedText: it says the
 * same generic thing every time, so the tooltip ConstrainedText brings has nothing to add.
 */
export const PlaceholderLabel = styled(Text)<{ $compact: boolean }>`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	${(p) => p.$compact && 'max-width: 100%;'}
`;

/** The shared scroll area, capped at the entry budget computed above. */
export const ListScroll = styled(SelectMenuScroll)`
	max-height: ${LIST_MAX_HEIGHT_REM}rem;
`;

/** The shared row, with the line height the two-line label budget assumes. */
export const DeploymentMenuItem = styled(SelectMenuRow)`
	line-height: ${MENU_ITEM_LINE_HEIGHT_REM}rem;
`;
